<template>
  <div class="column-editor-container">
    <a-spin :spinning="loading">
      <a-page-header
        :title="pageHeaderTitle"
        @back="handleBack"
      >
        <template slot="extra">
          <!-- <a-button @click="handleBack">取消</a-button> -->
          <CommonPermissions v-if="mode === 'basic'" :p="isEdit ? $permissions.COLUMN.UPDATE : $permissions.COLUMN.CREATE">
            <a-button type="primary" :loading="submitting" @click="handleSubmit">
              {{ isEdit ? '保存修改' : '创建专栏' }}
            </a-button>
          </CommonPermissions>
        </template>
      </a-page-header>

      <a-card v-if="mode === 'basic'" :bordered="false" style="margin-top: 16px;">
        <a-form-model
          ref="formRef"
          :model="form"
          :rules="rules"
          :label-col="{ span: 3 }"
          :wrapper-col="{ span: 18 }"
        >
          <a-form-model-item label="标题" prop="title">
            <a-input
              v-model="form.title"
              placeholder="请输入专栏标题"
              :maxLength="100"
              allowClear
            />
          </a-form-model-item>
          <a-form-model-item label="类型">
            <span>{{ typeName }}</span>
          </a-form-model-item>
          <a-form-model-item label="简介" prop="summary">
            <a-textarea
              v-model="form.summary"
              placeholder="请输入专栏简介"
              :rows="3"
              :maxLength="500"
              allowClear
            />
          </a-form-model-item>
          <a-form-model-item label="封面图" prop="thumbUri">
            <common-upload
              path="column/cover/"
              :defaultUrl="form.thumbUrl"
              @changeImgUri="handleCoverChange"
            />
          </a-form-model-item>
          <a-form-model-item label="是否免费" prop="free">
            <a-radio-group v-model="form.free">
              <a-radio :value="true">免费</a-radio>
              <a-radio :value="false">收费</a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-form-model-item v-if="!form.free" label="价格(元)" prop="price">
            <a-input-number
              v-model="form.price"
              :min="0"
              :precision="2"
              placeholder="请输入价格"
              style="width: 160px;"
            />
          </a-form-model-item>
          <a-form-model-item label="状态" prop="status">
            <a-radio-group v-model="form.status">
              <a-radio :value="0">禁用</a-radio>
              <a-radio :value="1">启用</a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-form-model-item label="排序" prop="sortKey">
            <a-input-number
              v-model="form.sortKey"
              :min="0"
              placeholder="数值越小越靠前"
              style="width: 160px;"
            />
          </a-form-model-item>
        </a-form-model>
      </a-card>

      <a-row v-if="isEdit && mode === 'content'" :gutter="16" style="margin-top: 16px;">
        <a-col :span="10">
          <a-card :bordered="false" title="专栏内容">
            <template slot="extra">
              <CommonPermissions :p="$permissions.COLUMN.CREATE">
                <a-button type="primary" size="small" @click="startCreateContent">
                  <a-icon type="plus" /> 新增内容
                </a-button>
              </CommonPermissions>
            </template>

            <a-tree
              v-if="contentTreeData.length"
              :tree-data="contentTreeData"
              :replace-fields="{ key: 'id', title: 'title', children: 'contentList' }"
              :selected-keys="selectedContentKeys"
              default-expand-all
              @select="handleContentSelect"
            >
              <template slot="title" slot-scope="{ title, contentType, freeTrial }">
                <span class="tree-node-title">{{ title }}</span>
                <a-tag size="small" class="tree-node-tag">{{ contentTypeLabel(contentType) }}</a-tag>
                <a-tag v-if="contentType !== 0 && freeTrial" size="small" color="green">试看</a-tag>
              </template>
            </a-tree>
            <a-empty v-else description="暂无内容，点击右上角按钮新增" />
          </a-card>
        </a-col>

        <a-col :span="14">
          <a-card :bordered="false" :title="contentEditing ? '编辑内容' : '内容详情'">
            <template slot="extra">
              <template v-if="selectedContentDetail">
                <CommonPermissions :p="$permissions.COLUMN.UPDATE">
                  <a-button v-if="!contentEditing" size="small" @click="startEditCurrentContent">编辑当前内容</a-button>
                </CommonPermissions>
                <CommonPermissions :p="$permissions.COLUMN.DELETE">
                  <a-popconfirm
                    title="确定删除当前内容吗？"
                    @confirm="deleteCurrentContent"
                  >
                    <a-button v-if="!contentEditing" type="danger" size="small" style="margin-left: 8px;">删除</a-button>
                  </a-popconfirm>
                </CommonPermissions>
              </template>
            </template>

            <a-spin :spinning="detailLoading">
              <a-form-model
                v-if="contentEditing"
                ref="contentEditRef"
                :model="contentEditForm"
                :rules="contentEditRules"
                :label-col="{ span: 5 }"
                :wrapper-col="{ span: 17 }"
              >
                <a-form-model-item label="标题" prop="title">
                  <a-input v-model="contentEditForm.title" placeholder="请输入内容标题" allowClear />
                </a-form-model-item>

                <a-form-model-item label="内容类型" prop="contentType">
                  <a-select
                    v-model="contentEditForm.contentType"
                    style="width: 220px;"
                    :disabled="contentEditMode === 'edit'"
                  >
                    <a-select-option :value="0">文件夹</a-select-option>
                    <a-select-option :value="1">视频</a-select-option>
                    <a-select-option :value="2">音频</a-select-option>
                    <a-select-option :value="3">AI问答链接</a-select-option>
                    <a-select-option :value="4">虚拟仿真地址</a-select-option>
                    <a-select-option :value="5">PPT</a-select-option>
                    <a-select-option :value="6">其他</a-select-option>
                  </a-select>
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType !== 0" label="所属文件夹" prop="folderId">
                  <a-select
                    v-model="contentEditForm.folderId"
                    placeholder="不选则为根级"
                    allowClear
                    style="width: 260px;"
                  >
                    <a-select-option
                      v-for="folder in availableFolderOptions"
                      :key="folder.id"
                      :value="folder.id"
                    >
                      {{ folder.title }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType === 1" label="视频资源" prop="contentUri">
                  <VideoUpload
                    path="video/column/content/"
                    :defaultUrl="contentEditForm.contentUrl"
                    @changeUri="handleContentVideoUriChange"
                  />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType === 2" label="音频资源" prop="contentUri">
                  <AudioUpload
                    path="audio/column/content/"
                    :defaultUrl="contentEditForm.contentUrl"
                    @changeUri="handleContentAudioUriChange"
                  />
                </a-form-model-item>

                <a-form-model-item
                  v-if="contentEditForm.contentType === 3 || contentEditForm.contentType === 4"
                  label="资源URL"
                  prop="contentUri"
                >
                  <a-input v-model="contentEditForm.contentUri" placeholder="请输入资源URL" allowClear />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType === 5" label="PPT资源" prop="contentUri">
                  <FileUpload
                    path="file/column/content/"
                    :defaultUrl="contentEditForm.contentUrl"
                    @changeUri="handleContentFileUriChange"
                  />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType !== 0" label="简介" prop="summary">
                  <a-textarea v-model="contentEditForm.summary" :rows="2" placeholder="选填" allowClear />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType !== 0" label="详细介绍" prop="detailContent">
                  <editor
                    v-model="contentEditForm.detailContent"
                    :init="detailEditorInit"
                  />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType !== 0" label="预览图" prop="thumbUri">
                  <Upload
                    :defaultUrl="contentEditForm.thumbUrl"
                    @changeImgUri="handleContentThumbChange"
                    path="img/column/cover/"
                  />
                </a-form-model-item>

                <a-form-model-item v-if="contentEditForm.contentType !== 0" label="是否试看" prop="freeTrial">
                  <a-switch v-model="contentEditForm.freeTrial" />
                </a-form-model-item>

                <a-form-model-item label="排序" prop="sortKey">
                  <a-input-number v-model="contentEditForm.sortKey" :min="0" style="width: 180px;" />
                </a-form-model-item>

                <a-form-model-item :wrapper-col="{ span: 17, offset: 5 }">
                  <CommonPermissions :p="contentEditMode === 'create' ? $permissions.COLUMN.CREATE : $permissions.COLUMN.UPDATE">
                    <a-button type="primary" :loading="contentSubmitting" @click="submitContentEdit">保存</a-button>
                  </CommonPermissions>
                  <a-button style="margin-left: 8px;" @click="cancelContentEdit">取消</a-button>
                </a-form-model-item>
              </a-form-model>

              <a-empty v-else-if="!selectedContentDetail" description="请选择左侧一个内容节点" />

              <a-descriptions v-else :column="1" bordered size="small">
                <a-descriptions-item label="标题">{{ selectedContentDetail.title || '-' }}</a-descriptions-item>
                <a-descriptions-item label="内容类型">{{ contentTypeLabel(selectedContentDetail.contentType) }}</a-descriptions-item>
                <a-descriptions-item label="排序">{{ selectedContentDetail.sortKey || 0 }}</a-descriptions-item>

                <template v-if="selectedContentDetail.contentType !== 0">
                  <a-descriptions-item label="所属文件夹">{{ selectedContentDetail.folderId || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="资源">
                    <template v-if="selectedContentDetail.contentType === 1">
                      <div v-if="selectedContentDetail.contentUrl || selectedContentDetail.contentUri" style="margin-top: 8px;">
                        <video
                          controls
                          style="max-width: 320px; width: 100%;"
                          :src="selectedContentDetail.contentUrl || selectedContentDetail.contentUri"
                        />
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-else-if="selectedContentDetail.contentType === 2">
                      <div v-if="selectedContentDetail.contentUrl || selectedContentDetail.contentUri" style="margin-top: 8px;">
                        <audio
                          controls
                          style="width: 100%; max-width: 320px;"
                          :src="selectedContentDetail.contentUrl || selectedContentDetail.contentUri"
                        />
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-else-if="selectedContentDetail.contentType === 3">
                      <span>{{ selectedContentDetail.contentUrl || selectedContentDetail.contentUri || '-' }}</span>
                    </template>
                    <template v-else-if="selectedContentDetail.contentType === 4">
                      <span>{{ selectedContentDetail.contentUrl || selectedContentDetail.contentUri || '-' }}</span>
                    </template>
                    <template v-else-if="selectedContentDetail.contentType === 5">
                      <a
                        v-if="selectedContentDetail.contentUrl || selectedContentDetail.contentUri"
                        :href="selectedContentDetail.contentUrl || selectedContentDetail.contentUri"
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <a-button size="small" type="primary" ghost>
                          <a-icon type="download" /> 下载PPT
                        </a-button>
                      </a>
                      <span v-else>-</span>
                    </template>
                    <span v-else>-</span>
                  </a-descriptions-item>
                  <a-descriptions-item label="简介">{{ selectedContentDetail.summary || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="预览图">
                    <img
                      v-if="selectedContentDetail.thumbUrl"
                      :src="selectedContentDetail.thumbUrl"
                      style="max-width: 260px; max-height: 180px; border-radius: 4px;"
                    />
                    <span v-else>-</span>
                  </a-descriptions-item>
                  <a-descriptions-item label="是否试看">
                    {{ selectedContentDetail.freeTrial ? '是' : '否' }}
                  </a-descriptions-item>
                  <a-descriptions-item label="详细介绍">
                    <div
                      v-if="selectedContentDetail.detailContent"
                      v-html="selectedContentDetail.detailContent"
                      class="detail-content-box"
                    />
                    <span v-else>-</span>
                  </a-descriptions-item>
                </template>
              </a-descriptions>
            </a-spin>
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import Upload from '@/components/Upload.vue'
import CommonPermissions from '@/components/CommonPermissions'
import AudioUpload from './upload/AudioUpload.vue'
import VideoUpload from './upload/VideoUpload.vue'
import FileUpload from './upload/FileUpload.vue'
import ColumnApi from '@/api/src/column/api/ColumnApi'
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/tinymce'
import 'tinymce/themes/silver/theme'
import 'tinymce/icons/default/icons'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/table'
import 'tinymce/plugins/code'
import 'tinymce/plugins/image'
import 'tinymce/plugins/media'
import 'tinymce/plugins/preview'

const columnApi = new ColumnApi()

export default {
  name: 'ColumnEditor',
  components: { Upload, CommonPermissions, AudioUpload, VideoUpload, FileUpload, Editor },
  data() {
    return {
      loading: false,
      submitting: false,
      isEdit: false,
      mode: 'basic',
      columnId: null,
      form: {
        title: '',
        type: undefined,
        summary: '',
        thumbUri: '',
        thumbUrl: '',
        free: true,
        price: undefined,
        status: 1,
        sortKey: 0,
      },
      rules: {
        title: [
          { required: true, message: '请输入专栏标题', trigger: 'blur' },
          { max: 100, message: '标题不能超过100个字符', trigger: 'blur' },
        ],
        free: [{ required: true, message: '请选择是否免费', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }],
      },

      contentTreeData: [],
      selectedContentId: null,
      selectedContentDetail: null,
      detailLoading: false,
      contentEditing: false,
      contentEditMode: 'edit',
      contentSubmitting: false,
      contentEditForm: {
        title: '',
        contentType: 0,
        folderId: undefined,
        contentUri: '',
        contentUrl: '',
        summary: '',
        detailContent: '',
        thumbUri: '',
        thumbUrl: '',
        freeTrial: false,
        sortKey: 0,
      },
      contentEditRules: {
        title: [{ required: true, message: '请输入内容标题', trigger: 'blur' }],
        contentType: [{ required: true, message: '请选择内容类型', trigger: 'change' }],
      },
      detailEditorInit: {
        skin_url: '/tinymce/skins/ui/oxide',
        language_url: '/tinymce/langs/zh_CN.js',
        language: 'zh_CN',
        menubar: 'file edit view insert format tools table',
        plugins: ['link', 'lists', 'table', 'code', 'image', 'media', 'preview'],
        toolbar: [
          'undo redo | formatselect | bold italic underline strikethrough',
          'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | preview code',
        ],
        height: 700,
        branding: false,
        paste_data_images: true,
        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; line-height: 1.6; }',
      },
    }
  },
  computed: {
    typeName() {
      const map = { 1: '数字课堂', 2: '数字资源', 3: '知识服务' }
      return map[this.form.type] || '未知类型'
    },
    /** 编辑时展示专栏标题；新建或无标题时用类型名占位 */
    pageHeaderTitle() {
      const prefix = this.isEdit ? '编辑专栏' : '新建专栏'
      const columnTitle = this.form.title && String(this.form.title).trim()
      const suffix = this.isEdit && columnTitle ? columnTitle : this.typeName
      return `${prefix} - ${suffix}`
    },
    listRouteName() {
      const map = { 1: 'ColumnClassroom', 2: 'ColumnResource', 3: 'ColumnKnowledge' }
      return map[this.form.type] || 'ColumnClassroom'
    },
    selectedContentKeys() {
      return this.selectedContentId ? [this.selectedContentId] : []
    },
    rootFolderOptions() {
      return (this.contentTreeData || [])
        .filter((node) => node && node.contentType === 0 && this.isRootFolderId(node.folderId))
        .map((node) => ({ id: node.id, title: node.title }))
    },
    availableFolderOptions() {
      if (this.contentEditMode !== 'edit' || !this.selectedContentId) {
        return this.rootFolderOptions
      }
      return this.rootFolderOptions.filter((item) => item.id !== this.selectedContentId)
    },
  },
  created() {
    const { id, mode, type } = this.$route.query
    console.log({ id, mode, type })
    this.mode = mode === 'content' ? 'content' : 'basic'
    if (id) {
      this.isEdit = true
      this.columnId = id
      this.fetchDetail()
      if (this.mode === 'content') {
        this.fetchContentList()
      }
    } else {
      if (type) this.form.type = Number(type)
      if (this.mode === 'content') this.mode = 'basic'
    }
  },
  methods: {
    contentTypeLabel(type) {
      const map = {
        0: '文件夹',
        1: '视频',
        2: '音频',
        3: 'AI问答',
        4: '虚拟仿真',
        5: 'PPT',
        6: '其他',
      }
      return map[type] || '未知'
    },
    async fetchDetail() {
      this.loading = true
      try {
        const resp = await columnApi.getColumnById(this.columnId)
        this.form = {
          title: resp.title || '',
          type: resp.type,
          summary: resp.summary || '',
          thumbUri: resp.thumbUri || '',
          thumbUrl: resp.thumbUrl || '',
          free: resp.free !== false,
          price: resp.price != null ? resp.price / 100 : undefined,
          status: resp.status !== undefined ? resp.status : 1,
          sortKey: resp.sortKey != null ? resp.sortKey : 0,
        }
      } catch (error) {
        console.error('获取专栏详情失败:', error)
        this.$message.error('获取专栏详情失败')
      } finally {
        this.loading = false
      }
    },
    async fetchContentList(preferredId) {
      try {
        const resp = await columnApi.getColumnContentList(this.columnId)
        const raw = Array.isArray(resp) ? resp : []
        const flat = this.flattenColumnContentList(raw)
        this.contentTreeData = this.buildContentTreeFromFlatList(flat)
        const nextId = preferredId || this.selectedContentId || this.getFirstNodeId(this.contentTreeData)
        if (nextId) {
          await this.loadContentDetail(nextId)
        } else {
          this.selectedContentId = null
          this.selectedContentDetail = null
          this.contentEditing = false
        }
      } catch (error) {
        console.error('获取专栏内容树失败:', error)
        this.$message.error('获取专栏内容树失败')
      }
    },
    /** 无 folderId / 0 表示一级（根）节点 */
    isRootFolderId(folderId) {
      return folderId == null || folderId === 0
    },
    /** 接口可能返回扁平列表或带 contentList 的嵌套，先拍平再按 folderId 建树 */
    flattenColumnContentList(list) {
      const out = []
      const walk = (nodes) => {
        if (!nodes || !nodes.length) return
        nodes.forEach((n) => {
          if (!n || n.id == null) return
          const { contentList, ...rest } = n
          out.push(rest)
          if (contentList && contentList.length) walk(contentList)
        })
      }
      walk(list)
      return out
    },
    /** folderId 为父节点 id；无 folderId 的为一级 */
    buildContentTreeFromFlatList(flat) {
      if (!flat || !flat.length) return []
      const byId = new Map()
      flat.forEach((item) => {
        if (!item || item.id == null) return
        byId.set(item.id, { ...item, contentList: [] })
      })
      const roots = []
      flat.forEach((item) => {
        if (!item || item.id == null) return
        const node = byId.get(item.id)
        const fid = item.folderId
        if (this.isRootFolderId(fid)) {
          roots.push(node)
        } else {
          const parent = byId.get(fid)
          if (parent) parent.contentList.push(node)
          else roots.push(node)
        }
      })
      const sortRec = (arr) => {
        arr.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0))
        arr.forEach((n) => {
          if (n.contentList && n.contentList.length) sortRec(n.contentList)
        })
      }
      sortRec(roots)
      return roots
    },
    getFirstNodeId(nodes) {
      if (!nodes || !nodes.length) return null
      return nodes[0].id
    },
    async handleContentSelect(selectedKeys) {
      if (!selectedKeys || !selectedKeys.length) return
      await this.loadContentDetail(selectedKeys[0])
      this.contentEditing = false
    },
    async loadContentDetail(id) {
      this.detailLoading = true
      try {
        const detail = await columnApi.getColumnContentById(id)
        console.log('detail')
        console.log(detail)
        this.selectedContentId = detail.id
        this.selectedContentDetail = detail
      } catch (error) {
        console.error('获取内容详情失败:', error)
        this.$message.error('获取内容详情失败')
      } finally {
        this.detailLoading = false
      }
    },
    startCreateContent() {
      this.contentEditMode = 'create'
      this.contentEditing = true
      this.contentEditForm = {
        title: '',
        contentType: 0,
        folderId: undefined,
        contentUri: '',
        contentUrl: '',
        summary: '',
        detailContent: '',
        thumbUri: '',
        thumbUrl: '',
        freeTrial: false,
        sortKey: 0,
      }
      this.$nextTick(() => {
        if (this.$refs.contentEditRef) this.$refs.contentEditRef.clearValidate()
      })
    },
    startEditCurrentContent() {
      if (!this.selectedContentDetail) return
      const detail = this.selectedContentDetail
      this.contentEditMode = 'edit'
      this.contentEditing = true
      this.contentEditForm = {
        title: detail.title || '',
        contentType: detail.contentType,
        folderId: detail.folderId || undefined,
        contentUri: detail.contentUri || '',
        contentUrl: detail.contentUrl || '',
        summary: detail.summary || '',
        detailContent: detail.detailContent || '',
        thumbUri: detail.thumbUri || '',
        thumbUrl: detail.thumbUrl || '',
        freeTrial: !!detail.freeTrial,
        sortKey: detail.sortKey || 0,
      }
      this.$nextTick(() => {
        if (this.$refs.contentEditRef) this.$refs.contentEditRef.clearValidate()
      })
    },
    cancelContentEdit() {
      this.contentEditing = false
    },
    buildContentPayload() {
      const isFolder = this.contentEditForm.contentType === 0
      const isOther = this.contentEditForm.contentType === 6
      return {
        columnId: this.columnId,
        folderId: isFolder ? undefined : (this.contentEditForm.folderId || undefined),
        title: this.contentEditForm.title,
        contentType: this.contentEditForm.contentType,
        contentUri: (isFolder || isOther) ? undefined : (this.contentEditForm.contentUri || undefined),
        summary: isFolder ? undefined : (this.contentEditForm.summary || undefined),
        detailContent: isFolder ? undefined : (this.contentEditForm.detailContent || undefined),
        thumbUri: isFolder ? undefined : (this.contentEditForm.thumbUri || undefined),
        freeTrial: isFolder ? undefined : !!this.contentEditForm.freeTrial,
        sortKey: this.contentEditForm.sortKey || 0,
      }
    },
    handleContentAudioUriChange(uri) {
      this.contentEditForm.contentUri = uri || ''
    },
    handleContentVideoUriChange(uri) {
      this.contentEditForm.contentUri = uri || ''
    },
    handleContentFileUriChange(uri) {
      this.contentEditForm.contentUri = uri || ''
    },
    handleContentThumbChange(uri) {
      this.contentEditForm.thumbUri = uri || ''
      if (!uri) {
        this.contentEditForm.thumbUrl = ''
      }
    },
    handleImageError(event) {
      // if (event && event.target) {
      //   event.target.style.display = 'none'
      // }
    },
    validateContentHierarchy(payload) {
      // 规则1：最多两级；规则2：第二级只能是非文件夹类型
      if (payload.folderId) {
        const isRootFolder = this.rootFolderOptions.some((item) => item.id === payload.folderId)
        if (!isRootFolder) {
          this.$message.error('所属文件夹仅支持选择一级文件夹')
          return false
        }
        if (payload.contentType === 0) {
          this.$message.error('第二级内容不能选择“文件夹”类型')
          return false
        }
      }
      return true
    },
    validateContentResource() {
      const type = this.contentEditForm.contentType
      // 视频、音频、AI问答、虚拟仿真、PPT都必须有资源地址（上传或输入）
      if ([1, 2, 3, 4, 5].includes(type) && !this.contentEditForm.contentUri) {
        this.$message.error('请先填写或上传资源')
        return false
      }
      // 其他类型（6）不需要填写 URL
      return true
    },
    submitContentEdit() {
      if (!this.$refs.contentEditRef) return
      this.$refs.contentEditRef.validate(async (valid) => {
        if (!valid) return
        this.contentSubmitting = true
        try {
          if (!this.validateContentResource()) {
            return
          }
          const payload = this.buildContentPayload()
          if (!this.validateContentHierarchy(payload)) {
            return
          }
          if (this.contentEditMode === 'create') {
            const created = await columnApi.addColumnContent(payload)
            this.$message.success('新增成功')
            await this.fetchContentList(created && created.id ? created.id : undefined)
          } else {
            await columnApi.updateColumnContent(this.selectedContentId, payload)
            this.$message.success('修改成功')
            await this.fetchContentList(this.selectedContentId)
          }
          this.contentEditing = false
        } catch (error) {
          console.error('保存内容失败:', error)
          this.$message.error('保存内容失败')
        } finally {
          this.contentSubmitting = false
        }
      })
    },
    async deleteCurrentContent() {
      if (!this.selectedContentId) return
      try {
        await columnApi.deleteColumnContent(this.selectedContentId)
        this.$message.success('删除成功')
        this.selectedContentId = null
        this.selectedContentDetail = null
        this.contentEditing = false
        await this.fetchContentList()
      } catch (error) {
        console.error('删除内容失败:', error)
        this.$message.error('删除内容失败')
      }
    },
    handleCoverChange(uri) {
      this.form.thumbUri = uri
      if (!uri) this.form.thumbUrl = ''
    },
    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return
        this.submitting = true
        try {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.$router.push({ name: this.listRouteName })
        } finally {
          this.submitting = false
        }
      })
    },
    handleBack() {
      this.$router.back()
    },
  },
}
</script>

<style lang="less" scoped>
.column-editor-container {
  padding: 0 24px 24px;

  /deep/ .ant-page-header {
    padding: 16px 0;
    background: transparent;
  }
}

.tree-node-title {
  margin-right: 8px;
}

.tree-node-tag {
  margin-right: 4px;
}

.detail-content-box {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
}
</style>
